import React, { useState, useRef, useEffect } from 'react';
import '../styles/ProfileImageUploadModal.css';

const ProfileImageUploadModal = ({ isOpen, onClose, onUpload, isLoading }) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    
    // Clean up preview URL when modal closes
    useEffect(() => {
        return () => {
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Basic validation
        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('Only JPEG, PNG, and WebP images are supported');
            setSelectedFile(null);
            setPreviewUrl(null);
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB limit
            setError('Image size must be less than 5MB');
            setSelectedFile(null);
            setPreviewUrl(null);
            return;
        }

        // Create preview
        const preview = URL.createObjectURL(file);
        setSelectedFile(file);
        setPreviewUrl(preview);
        setError(null);
    };

    const handleUpload = () => {
        if (!selectedFile) {
            setError('Please select an image file');
            return;
        }

        onUpload(selectedFile);
    };

    const handleRemoveFile = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (!isOpen) return null;

    return (
        <div className="profile-image-upload-modal-overlay">
            <div className="profile-image-upload-modal">
                <div className="modal-header">
                    <h3>Upload Profile Image</h3>
                    <button className="close-button" onClick={onClose} disabled={isLoading}>
                        ×
                    </button>
                </div>

                <div className="modal-content">
                    {!previewUrl ? (
                        <div className="file-upload-area">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept="image/jpeg,image/png,image/webp"
                                disabled={isLoading}
                                style={{ display: 'none' }}
                                id="profile-image-upload-input"
                            />
                            <label htmlFor="profile-image-upload-input" className="upload-label">
                                <div className="upload-icon">📁</div>
                                <p>Click to select an image</p>
                                <p className="file-types">JPEG, PNG, or WebP (Max 5MB)</p>
                            </label>
                        </div>
                    ) : (
                        <div className="image-preview-container">
                            <img src={previewUrl} alt="Preview" className="image-preview" />
                            <button
                                className="remove-image-button"
                                onClick={handleRemoveFile}
                                disabled={isLoading}
                            >
                                Remove Image
                            </button>
                        </div>
                    )}

                    {error && <div className="error-message">{error}</div>}

                    <div className="modal-actions">
                        <button
                            className="cancel-button"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            className="upload-button"
                            onClick={handleUpload}
                            disabled={!selectedFile || isLoading}
                        >
                            {isLoading ? 'Uploading...' : 'Upload Image'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileImageUploadModal;